import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Users,
  Clock,
  BarChart3,
  BookOpen,
  Settings,
  Download,
  Loader,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { apiService } from "../../services/api";
import "./AssessmentManagement.css";

const AssessmentManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [assessments, setAssessments] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Fetch assessments and skills from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch assessments and skills in parallel
        const [assessmentsResponse, skillsResponse] = await Promise.all([
          apiService.assessments.getAllAssessments(),
          apiService.assessments.getSkills(),
        ]);

        if (assessmentsResponse && !assessmentsResponse.error) {
          setAssessments(assessmentsResponse);
        } else {
          throw new Error(
            assessmentsResponse?.error || "Failed to fetch assessments",
          );
        }

        if (skillsResponse && !skillsResponse.error) {
          setSkills(skillsResponse);
        } else {
          console.warn("Failed to fetch skills:", skillsResponse?.error);
          setSkills([]);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to load assessments");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Manual refresh function
  const refreshAssessments = async () => {
    try {
      setRefreshing(true);
      const assessmentsResponse =
        await apiService.assessments.getAllAssessments();
      if (assessmentsResponse && !assessmentsResponse.error) {
        setAssessments(assessmentsResponse);
      }
    } catch (err) {
      console.error("Error refreshing assessments:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Get skill category for an assessment
  const getSkillCategory = (skillId) => {
    const skill = skills.find((s) => s.id === skillId);
    return skill?.category || "Unknown";
  };

  // Action handlers
  const handleViewAssessment = (assessmentId) => {
    navigate(`/admin/assessment-details/${assessmentId}`);
  };

  const handleEditAssessment = (assessmentId) => {
    // For now, redirect to create assessment page with edit mode
    // TODO: Create dedicated edit assessment page or modify CreateAssessment to handle editing
    navigate(`/admin/create-assessment?edit=${assessmentId}`);
  };

  const handleDeleteAssessment = async (assessmentId, assessmentTitle) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${assessmentTitle}"? This action cannot be undone.`,
      )
    ) {
      try {
        setDeleting(assessmentId);

        const response =
          await apiService.assessments.deleteAssessment(assessmentId);

        if (response && !response.error) {
          // Remove from local state
          setAssessments((prev) => prev.filter((a) => a.id !== assessmentId));
          console.log("Assessment deleted successfully:", response.message);
        } else {
          throw new Error(response?.error || "Failed to delete assessment");
        }
      } catch (error) {
        console.error("Error deleting assessment:", error);
        alert(`Failed to delete assessment: ${error.message}`);
      } finally {
        setDeleting(null);
      }
    }
  };

  const handleExportData = () => {
    try {
      const dataStr = JSON.stringify(assessments, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessments-export-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting data:", error);
      alert("Failed to export data. Please try again.");
    }
  };

  // Get unique categories from skills
  const categories = [
    "All",
    ...new Set(skills.map((skill) => skill.category).filter(Boolean)),
  ];
  const statuses = ["All", "Active", "Draft", "Archived"];

  const filteredAssessments = assessments.filter((assessment) => {
    const matchesSearch = assessment.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const skillCategory = getSkillCategory(assessment.skillId);
    const matchesCategory =
      selectedCategory === "All" || skillCategory === selectedCategory;

    // For now, treat all assessments as "Active" since we don't have status in DB
    const assessmentStatus = "Active";
    const matchesStatus =
      selectedStatus === "All" || assessmentStatus === selectedStatus;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Calculate stats from real data
  const totalCompletions = 0; // TODO: Add completion tracking
  const avgScore = assessments.length > 0 ? 78 : 0; // TODO: Calculate from real data
  const avgDuration =
    assessments.length > 0
      ? Math.round(
          assessments.reduce(
            (sum, a) => sum + (a.timeLimit ? a.timeLimit / 60 : 30),
            0,
          ) / assessments.length,
        )
      : 0;

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner":
        return "#10b981";
      case "intermediate":
        return "#f59e0b";
      case "advanced":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "#10b981";
      case "Draft":
        return "#f59e0b";
      case "Archived":
        return "#6b7280";
      default:
        return "#6b7280";
    }
  };

  if (loading) {
    return (
      <div className="assessment-management">
        <div className="loading-state">
          <Loader size={48} className="spinner" />
          <h3>Loading assessments...</h3>
          <p>Please wait while we fetch the latest assessments</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-management">
        <div className="error-state">
          <AlertCircle size={48} />
          <h3>Failed to load assessments</h3>
          <p>{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-management">
      <div className="assessment-header">
        <div className="header-content">
          <h1>Assessment Management</h1>
          <p>Create, manage, and monitor your assessments</p>
        </div>
        <div className="header-actions">
          <button
            className="refresh-btn"
            onClick={refreshAssessments}
            disabled={refreshing}
            title="Refresh assessments"
          >
            <RefreshCw size={18} className={refreshing ? "spinning" : ""} />
          </button>
          <button className="btn btn-secondary" onClick={handleExportData}>
            <Download size={18} />
            Export Data
          </button>
          <Link to="/admin/create-assessment" className="btn btn-primary">
            <Plus size={18} />
            Create Assessment
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="assessment-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <BookOpen size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{assessments.length}</div>
            <div className="stat-label">Total Assessments</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {totalCompletions.toLocaleString()}
            </div>
            <div className="stat-label">Total Completions</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <BarChart3 size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{avgScore}%</div>
            <div className="stat-label">Average Score</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{avgDuration} min</div>
            <div className="stat-label">Avg Duration</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="assessment-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search assessments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <Filter size={18} />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Assessments Table */}
      <div className="assessments-table">
        <div className="table-header">
          <div className="table-cell">Assessment</div>
          <div className="table-cell">Category</div>
          <div className="table-cell">Questions</div>
          <div className="table-cell">Duration</div>
          <div className="table-cell">Difficulty</div>
          <div className="table-cell">Status</div>
          {/* <div className="table-cell">Completions</div> */}

          <div className="table-cell">Actions</div>
        </div>
        {filteredAssessments.map((assessment) => (
          <div key={assessment.id} className="table-row">
            <div className="table-cell">
              <div className="assessment-info">
                <div className="assessment-title">{assessment.title}</div>
                <div className="assessment-meta">
                  Created: {new Date(assessment.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="table-cell">
              <span className="category-badge">
                {getSkillCategory(assessment.skillId)}
              </span>
            </div>
            <div className="table-cell">
              <span className="question-count">
                {assessment.questionCount || 0}
              </span>
            </div>
            <div className="table-cell">
              <span className="duration">
                {assessment.timeLimit
                  ? Math.round(assessment.timeLimit / 60)
                  : 30}{" "}
                min
              </span>
            </div>
            <div className="table-cell">
              <span
                className="difficulty-badge"
                style={{
                  backgroundColor: getDifficultyColor(
                    assessment.difficultyLevel,
                  ),
                }}
              >
                {assessment.difficultyLevel || "Beginner"}
              </span>
            </div>
            <div className="table-cell">
              <span
                className="status-badge"
                style={{ backgroundColor: getStatusColor("Active") }}
              >
                Active
              </span>
            </div>
            {/* <div className="table-cell">
              <span className="completion-count">0</span>
            </div> */}

            <div className="table-cell">
              <div className="action-buttons">
                <button
                  className="action-btn view"
                  title="View Details"
                  onClick={() => handleViewAssessment(assessment.id)}
                >
                  <Eye size={16} />
                </button>
                <button
                  className="action-btn edit"
                  title="Edit Assessment"
                  onClick={() => handleEditAssessment(assessment.id)}
                >
                  <Edit size={16} />
                </button>
                <button className="action-btn settings" title="Settings">
                  <Settings size={16} />
                </button>
                <button
                  className="action-btn delete"
                  title="Delete Assessment"
                  onClick={() =>
                    handleDeleteAssessment(assessment.id, assessment.title)
                  }
                  disabled={deleting === assessment.id}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredAssessments.length === 0 && (
        <div className="empty-state">
          <BookOpen size={48} />
          <h3>No assessments found</h3>
          <p>Try adjusting your search criteria or create a new assessment.</p>
        </div>
      )}
    </div>
  );
};

export default AssessmentManagement;
